import React from "react";
import "./App.css";
import { Logo } from "./components/Logo";
import { Footer } from "./components/Footer";
import { Link } from "react-router-dom";
import styled from "styled-components";

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f5e6d3 0%, #f9f5f0 100%);
  position: relative;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-image: url("/images/hero-bg.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f5e6d3;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(245, 230, 211, 0.3);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 800px;
  padding: 0 20px;
`;

const Tagline = styled.p`
  font-family: "Noto Serif JP", serif;
  font-size: 20px;
  color: #6d4c41;
  margin-top: 30px;
  letter-spacing: 3px;
  font-weight: 300;
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 50px;
  border: 2px solid #4a5d23;
  border-radius: 25px;
  opacity: 0.6;
  cursor: pointer;
  z-index: 2;

  &::before {
    content: "";
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 10px;
    background: #4a5d23;
    border-radius: 2px;
    animation: scroll 1.5s ease-in-out infinite;
  }

  @keyframes scroll {
    0% {
      transform: translateX(-50%) translateY(0);
    }
    50% {
      transform: translateX(-50%) translateY(10px);
    }
    100% {
      transform: translateX(-50%) translateY(0);
    }
  }
`;

const ProductsSection = styled.section`
  position: relative;
  padding: 120px 0;
  background-image: url("/images/pattern-bg.png");
  background-repeat: repeat;
  background-size: 600px;
  background-position: center;
`;

const Container = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
`;

const SectionTitle = styled.h2`
  font-family: "Noto Serif JP", serif;
  font-size: 42px;
  color: #3e2723;
  text-align: center;
  margin-bottom: 20px;
  letter-spacing: 4px;
  font-weight: 300;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  color: #6d4c41;
  font-size: 16px;
  margin-bottom: 80px;
  letter-spacing: 2px;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
  margin-bottom: 80px;
`;

const ProductCard = styled.div`
  position: relative;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const ProductImageContainer = styled.div<{ bgImage: string }>`
  position: relative;
  width: 100%;
  height: 200px;
  background-image: url(${(props) => props.bgImage});
  background-size: cover;
  background-position: center;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      180deg,
      transparent 50%,
      rgba(62, 39, 35, 0.3) 100%
    );
  }
`;

const ProductInfo = styled.div`
  padding: 30px;
  background: rgba(255, 255, 255, 0.95);
`;

const ProductName = styled.h3`
  font-family: "Noto Sans JP", sans-serif;
  font-size: 20px;
  color: #3e2723;
  margin-bottom: 10px;
  font-weight: 500;
`;

const ProductDescription = styled.p`
  font-size: 14px;
  color: #6d4c41;
  line-height: 1.6;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  display: block;
  height: 100%;
`;

const CTASection = styled.section`
  padding: 80px 0;
  background: #4a5d23;
  text-align: center;
`;

const CTATitle = styled.h3`
  font-family: "Noto Serif JP", serif;
  font-size: 32px;
  color: #f5e6d3;
  margin-bottom: 20px;
  letter-spacing: 2px;
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 16px 48px;
  background: linear-gradient(135deg, #c9b037 0%, #d4a574 100%);
  color: #3e2723;
  text-decoration: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  margin: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(201, 176, 55, 0.3);
  }
`;

const RetailersSection = styled.section`
  padding: 80px 0;
  background: #f9f5f0;
  text-align: center;
`;

const RetailersList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  max-width: 800px;
  margin: 0 auto;
`;

const RetailerLink = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 12px 24px;
  background: white;
  border: 2px solid #4a5d23;
  border-radius: 30px;
  color: #4a5d23;
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: #4a5d23;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(74, 93, 35, 0.2);
  }
`;

const DecorativeLeft = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 200px;
  height: 800px;
  background-image: url("/images/botanical-left.png");
  background-repeat: no-repeat;
  background-position: center;
  opacity: 0.3;
  pointer-events: none;
`;

const DecorativeRight = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 200px;
  height: 800px;
  background-image: url("/images/botanical-left.png");
  background-repeat: no-repeat;
  background-position: center;
  opacity: 0.3;
  pointer-events: none;
`;

function App() {
  const products = [
    {
      path: "/ingredient/00001",
      name: "ラベンダー オイル CG",
      description: "穏やかな香りで心を落ち着かせる",
      bgImage: "/images/product-lavender-cg.jpg",
    },
    {
      path: "/ingredient/00002",
      name: "イランイラン",
      description: "エキゾチックで甘い花の香り",
      bgImage: "/images/product-ylang-ylang.jpg",
    },
    {
      path: "/ingredient/00003",
      name: "ユーカリグロブルス",
      description: "清涼感のある爽やかな香り",
      bgImage: "/images/product-eucalyptus.jpg",
    },
    {
      path: "/ingredient/00004",
      name: "レモングラス",
      description: "フレッシュなシトラスの香り",
      bgImage: "/images/product-lemongrass.jpg",
    },
    {
      path: "/ingredient/00005",
      name: "ローズアブソリュート",
      description: "高貴で優雅なバラの香り",
      bgImage: "/images/product-rose.jpg",
    },
    {
      path: "/ingredient/00006",
      name: "ブルガリア産 ラベンダー",
      description: "深みのある本格的なラベンダー",
      bgImage: "/images/product-lavender-bg.jpg",
    },
    {
      path: "/ingredient/00007",
      name: "フランス産 ラベンダー",
      description: "繊細で上品なラベンダーの香り",
      bgImage: "/images/product-lavender-fr1.jpg",
    },
    {
      path: "/ingredient/00008",
      name: "フランス産 ラベンダー",
      description: "プロヴァンス地方の優しい香り",
      bgImage: "/images/product-lavender-fr2.jpg",
    },
    {
      path: "/ingredient/00009",
      name: "ユーカリグロブルス（ロット9）",
      description: "清涼感のある爽やかな香り",
      bgImage: "/images/product-eucalyptus.jpg",
    },
    {
      path: "/ingredient/00010",
      name: "レモングラス（ロット10）",
      description: "フレッシュなシトラスの香り",
      bgImage: "/images/product-lemongrass.jpg",
    },
    {
      path: "/ingredient/00011",
      name: "イランイラン（ロット11）",
      description: "エキゾチックで甘い花の香り",
      bgImage: "/images/product-ylang-ylang.jpg",
    },
    {
      path: "/ingredient/00012",
      name: "ブルガリア産 ラベンダー（ロット12）",
      description: "深みのある本格的なラベンダー",
      bgImage: "/images/product-lavender-bg.jpg",
    },
    {
      path: "/ingredient/00013",
      name: "イタリア産 オレンジ（ロット13）",
      description: "甘くフレッシュなシトラスの香り",
      bgImage: "/images/product-orange.jpg",
    },
    {
      path: "/ingredient/00014",
      name: "ティーツリー（ロット14）",
      description: "清潔感のあるシャープな香り",
      bgImage: "/images/product-teatree.jpg",
    },
    {
      path: "/ingredient/00015",
      name: "ブルボン産 ゼラニウム（ロット15）",
      description: "ローズに似た華やかなフローラルの香り",
      bgImage: "/images/product-geranium.jpg",
    },
    {
      path: "/ingredient/00016",
      name: "グレープフルーツ（ロット16）",
      description: "フレッシュで爽やかなシトラスの香り",
      bgImage: "/images/product-grapefruit.jpg",
    },
    {
      path: "/ingredient/00017",
      name: "フランキンセンス（ロット17）",
      description: "神秘的で深みのあるウッディな香り",
      bgImage: "/images/product-frankincense.jpg",
    },
    {
      path: "/ingredient/00018",
      name: "フランス産 ラベンダー（ロット18・USDA認証）",
      description: "USDAオーガニック認証取得のプロヴァンス産ラベンダー",
      bgImage: "/images/product-lavender-fr2.jpg",
    },
    {
      path: "/ingredient/00019",
      name: "イランイラン（ロット19・USDA認証）",
      description: "USDAオーガニック認証取得のエキゾチックで甘い花の香り",
      bgImage: "/images/product-ylang-ylang.jpg",
    },
  ];

  const scrollToProducts = () => {
    const element = document.getElementById("products");
    element?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <Logo />
          <Tagline>自然の恵みを、暮らしの中に</Tagline>
        </HeroContent>
        <ScrollIndicator onClick={scrollToProducts} />
      </HeroSection>

      <ProductsSection id="products">
        <DecorativeLeft />
        <DecorativeRight />
        <Container>
          <SectionTitle>精油カタログ</SectionTitle>
          <SectionSubtitle>ESSENTIAL OIL COLLECTION</SectionSubtitle>

          <ProductGrid>
            {products.map((product, index) => (
              <ProductCard key={index}>
                <StyledLink to={product.path}>
                  <ProductImageContainer bgImage={product.bgImage} />
                  <ProductInfo>
                    <ProductName>{product.name}</ProductName>
                    <ProductDescription>
                      {product.description}
                    </ProductDescription>
                  </ProductInfo>
                </StyledLink>
              </ProductCard>
            ))}
          </ProductGrid>
        </Container>
      </ProductsSection>

      <CTASection>
        <CTATitle>精油について、もっと知る</CTATitle>
        <div>
          <CTAButton
            href="https://docs.google.com/forms/d/1ovnZTe0DKDbGf12tQ1_dHN4uwCa4wTWpfkwXQ0ADE88/edit"
            target="_blank"
            rel="noreferrer"
          >
            アンケートに答える
          </CTAButton>
          <CTAButton
            href="https://www.amazon.co.jp/vdp/230aac5b85ce454088b26378e9ccad66?product=B09PVL32WZ&ref=cm_sw_tw_r_ib_dt_XNbcSHxHubFmh"
            target="_blank"
            rel="noreferrer"
          >
            使い方を動画で見る
          </CTAButton>
        </div>
      </CTASection>

      <RetailersSection>
        <Container>
          <SectionTitle>販売先</SectionTitle>
          <SectionSubtitle>WHERE TO BUY</SectionSubtitle>
          <RetailersList>
            <RetailerLink
              href="https://www.amazon.co.jp/dp/B09PVL32WZ"
              target="_blank"
              rel="noreferrer"
            >
              真正ラベンダー オーガニック ACO認定 精油 10ml
            </RetailerLink>
            <RetailerLink
              href="https://www.amazon.co.jp/s?k=damascena+%E7%B2%BE%E6%B2%B9&ref=nb_sb_noss"
              target="_blank"
              rel="noreferrer"
            >
              その他のdamascena製品を検索
            </RetailerLink>
          </RetailersList>
        </Container>
      </RetailersSection>
      <Footer />
    </PageContainer>
  );
}

export default App;
